import React, { useEffect } from "react";

const NexusOracleFusion = () => {
  useEffect(() => {
    const reduce = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const reveals = Array.from(document.querySelectorAll("[data-reveal]"));

    if (reduce || !("IntersectionObserver" in window)) {
      reveals.forEach((el) => el.classList.add("in"));
      document.querySelectorAll("[data-count]").forEach((el) => {
        el.textContent = el.getAttribute("data-count");
      });
      return undefined;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.14, rootMargin: "0px 0px -8% 0px" },
    );

    reveals.forEach((el) => io.observe(el));

    const animationFrames = new Map();

    function countUp(el) {
      const target = parseInt(el.getAttribute("data-count"), 10);
      const dur = 1100;
      let start = null;

      function step(ts) {
        if (!start) start = ts;
        const p = Math.min((ts - start) / dur, 1);
        el.textContent = Math.round(target * (0.2 + 0.8 * p * (2 - p)));
        if (p < 1) {
          const frame = requestAnimationFrame(step);
          animationFrames.set(el, frame);
        } else {
          el.textContent = target;
          animationFrames.delete(el);
        }
      }

      const frame = requestAnimationFrame(step);
      animationFrames.set(el, frame);
    }

    const cio = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            countUp(entry.target);
            cio.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.6 },
    );

    document.querySelectorAll("[data-count]").forEach((el) => cio.observe(el));

    return () => {
      io.disconnect();
      cio.disconnect();
      animationFrames.forEach((frame) => cancelAnimationFrame(frame));
      animationFrames.clear();
    };
  }, []);

  return (
    <>
      <style>{`
  :root{
    --canvas:#F8FAFC;    /* Cool Slate */
    --surface:#FFFFFF;   /* Pure White  */
    --border:#E2E8F0;    /* Slate Hairline */
    --ink:#0F172A;       /* TechProjects Navy */
    --ink-mut:#434253;   /* TechProjects Slate */
    --primary:#F42F39;   /* TechProjects Red */
    --primary-hover:#e94e3c;
    --primary-soft:#FDDFDF;
    --accent-soft:#EAF0FF;
    --amber:#B8843A;
    --amber-soft:#F3E9D7;
    --font:"Roboto",-apple-system,BlinkMacSystemFont,"Segoe UI",Arial,sans-serif;
    --maxw:1160px;
    --shadow:0 10px 30px rgba(15,23,42,.08);
    --shadow-sm:0 4px 14px rgba(15,23,42,.06);
  }
  *{box-sizing:border-box;}
  html{scroll-behavior:smooth;}
  body{
    margin:0;background:var(--canvas);color:var(--ink);font-family:var(--font);
    font-size:17px;line-height:1.6;-webkit-font-smoothing:antialiased;
  }
  .wrap{max-width:var(--maxw);margin:0 auto;padding:0 24px;}
  section{padding:76px 0;}
  .eyebrow{
    font-size:13px;font-weight:700;letter-spacing:.18em;text-transform:uppercase;
    color:var(--accent1);margin:0 0 14px;
  }
  h1,h2,h3{margin:0;font-weight:800;letter-spacing:-.01em;line-height:1.12;}
  h1{font-size:clamp(34px,5vw,52px);}
  h2{font-size:clamp(28px,3.6vw,40px);}
  h3{font-size:20px;font-weight:700;letter-spacing:0;}
  p{margin:0 0 16px;}
  .lead{font-size:19px;color:var(--ink-mut);max-width:60ch;}
  a{color:var(--accent1);text-decoration:none;}
  a:focus-visible,button:focus-visible{outline:3px solid var(--accent1);outline-offset:3px;border-radius:6px;}

  /* buttons */
  .btn{
    display:inline-flex;align-items:center;gap:10px;font-weight:700;font-size:16px;
    padding:14px 26px;border-radius:40px;border:2px solid transparent;cursor:pointer;
    transition:transform .15s ease, box-shadow .15s ease, background .15s ease;
  }
  .btn-primary{background:var(--primary);color:#fff;box-shadow:var(--shadow-sm);}
  .btn-primary:hover{transform:translateY(-2px);background:var(--primary-hover);box-shadow:0 12px 24px rgba(244,47,57,.30);}
  .btn-ghost{background:transparent;color:var(--ink);border-color:var(--border);}
  .btn-ghost:hover{border-color:var(--accent1);color:var(--accent1);}
  .cta-row{display:flex;flex-wrap:wrap;gap:14px;margin-top:26px;}

  /* top brand line */
  .topbar{border-bottom:none;background:transparent;padding-top:22px;}
  .topbar-inner{background:var(--accent1);border-radius:20px;display:flex;align-items:center;justify-content:space-between;gap:18px;padding:14px 26px;box-shadow:var(--shadow);}
  .tp-logo{font-weight:800;font-size:19px;color:#fff;letter-spacing:-.01em;flex:none;}
  .tp-logo span{font-weight:400;opacity:.85;}
  .tp-nav{display:flex;align-items:center;gap:2px;}
  .tp-nav>a{color:rgba(255,255,255,.82);font-weight:600;font-size:14.5px;padding:9px 14px;border-radius:10px;transition:background .15s ease,color .15s ease;white-space:nowrap;}
  .tp-nav>a:hover{background:rgba(255,255,255,.12);color:#fff;}
  .tp-nav>a.active{background:rgba(255,255,255,.16);color:#fff;}
  .topbar .btn.sm{padding:10px 20px;font-size:14.5px;flex:none;}

  /* Products dropdown */
  .tp-dd{position:relative;}
  .tp-ddbtn{display:flex;align-items:center;gap:6px;color:#fff;background:rgba(255,255,255,.16);font-weight:600;font-size:14.5px;padding:9px 14px;border-radius:10px;border:none;cursor:pointer;font-family:var(--font);white-space:nowrap;}
  .tp-ddbtn svg{width:14px;height:14px;transition:transform .15s ease;}
  .tp-dd:hover .tp-ddbtn svg,.tp-dd:focus-within .tp-ddbtn svg{transform:rotate(180deg);}
  .crumb{margin:16px 0 0;font-size:13.5px;color:var(--ink-mut);}
  .crumb a{color:var(--ink-mut);}
  .crumb a:hover{color:var(--accent1);}
  .crumb span{color:var(--ink);font-weight:700;}
  @media (max-width:820px){
    .tp-nav{display:none;}
    .tp-logo{font-size:17px;}
  }

  /* hero */
  .hero{padding-top:150px;padding-bottom:40px;text-align:center;}
  .hero .lead{margin-left:auto;margin-right:auto;}
  .kicker{
    margin:14px auto 18px;font-size:clamp(17px,2vw,21px);font-weight:800;
    color:var(--accent1);letter-spacing:.01em;
  }
  .hero .cta-row{justify-content:center;}
  .trust{margin-top:22px;color:var(--ink-mut);font-size:14.5px;font-weight:500;}
  .trust b{color:var(--ink);}
  .hero-anim{
    margin:44px auto 0;background:var(--canvas);border:1px solid var(--border);
    border-radius:22px;box-shadow:var(--shadow);overflow:hidden;
  }
  .hero-anim svg{display:block;width:100%;height:auto;}

  /* positioning band */
  .band{background:var(--ink);color:var(--canvas);padding:40px 0;}
  .band .wrap{display:flex;align-items:center;gap:18px;}
  .band .marker{width:14px;height:14px;border-radius:50%;background:var(--primary);flex:none;box-shadow:0 0 0 6px rgba(244,47,57,.22);}
  .band p{margin:0;font-size:clamp(19px,2.4vw,26px);font-weight:600;line-height:1.35;}
  .band b{color:#fff;}


  /* problem cards */
  .grid{display:grid;gap:18px;}
  .cols-3{grid-template-columns:repeat(3,1fr);}
  .cols-2{grid-template-columns:repeat(2,1fr);}
  .card{
    background:var(--surface);border:1px solid var(--border);border-radius:16px;
    padding:24px;box-shadow:var(--shadow-sm);
  }
  .card h3{margin-bottom:6px;}
  .card p{margin:0;color:var(--ink-mut);font-size:15.5px;}
  .ic{
    width:44px;height:44px;border-radius:12px;display:flex;align-items:center;justify-content:center;
    margin-bottom:14px;background:var(--primary-soft);
  }
  .ic svg{width:22px;height:22px;}
  .note{
    margin-top:26px;background:var(--primary);color:#fff;border-radius:14px;
    padding:18px 24px;font-size:17px;font-weight:600;
  }

  /* solution mapping */
  .map{display:grid;grid-template-columns:1fr 1.4fr;border:1px solid var(--border);border-radius:16px;overflow:hidden;background:var(--surface);box-shadow:var(--shadow-sm);}
  .map .mh{padding:16px 22px;font-weight:700;color:#fff;font-size:15px;}
  .map .mh.a{background:var(--ink);}
  .map .mh.b{background:var(--primary);}
  .map .mrow{padding:16px 22px;border-top:1px solid var(--border);display:flex;align-items:center;font-size:16px;}
  .map .mrow.left{font-weight:700;}
  .map .mrow.right{background:var(--primary-soft);}
  .punch{margin-top:24px;font-size:22px;font-weight:800;color:var(--primary);letter-spacing:-.01em;}

  /* how it works steps */
  .steps{display:grid;gap:16px;}
  .step{
    display:grid;grid-template-columns:auto 1fr;gap:20px;align-items:start;
    background:var(--surface);border:1px solid var(--border);border-radius:16px;padding:24px 26px;box-shadow:var(--shadow-sm);
  }
  .step .num{
    width:46px;height:46px;border-radius:50%;background:var(--accent1);color:#fff;
    display:flex;align-items:center;justify-content:center;font-weight:800;font-size:18px;flex:none;
  }
  .step.last .num{background:var(--accent1);}
  .step h3{margin-bottom:5px;}
  .step p{margin:0;color:var(--ink-mut);font-size:15.5px;}

  /* coverage board */
  .board{display:grid;grid-template-columns:repeat(3,1fr);gap:18px;}
  .col{background:var(--surface);border:1px solid var(--border);border-radius:16px;padding:22px;box-shadow:var(--shadow-sm);display:flex;flex-direction:column;}
  .pill{display:flex;align-items:center;gap:10px;padding:11px 16px;border-radius:30px;color:#fff;font-weight:700;font-size:15px;margin-bottom:8px;}
  .pill.now{background:var(--accent1);}
  .pill.prog{background:var(--amber);}
  .pill.next{background:var(--primary);}
  .pill svg{width:17px;height:17px;}
  .col .sub{font-size:11.5px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:var(--ink-mut);margin:8px 2px 14px;}
  .item{margin-bottom:16px;}
  .item .t{font-weight:700;font-size:15.5px;margin-bottom:3px;}
  .item .d{font-size:14px;color:var(--ink-mut);line-height:1.5;}
  .reinforce{margin-top:auto;background:var(--accent-soft);border-radius:12px;padding:14px 16px;font-weight:700;font-style:italic;color:var(--accent);font-size:14px;}
  .trajectory{margin-top:26px;text-align:center;font-size:16px;color:var(--ink);}
  .trajectory b{color:var(--primary);}

  /* magnitude */
  .stats{display:grid;grid-template-columns:repeat(3,1fr);gap:18px;margin-bottom:34px;}
  .stat{background:var(--surface);border:1px solid var(--border);border-radius:16px;padding:26px;text-align:center;box-shadow:var(--shadow-sm);}
  .stat .big{font-size:clamp(30px,3.4vw,40px);font-weight:800;color:var(--accent1);letter-spacing:-.02em;line-height:1.05;}
  .stat .big .was{color:var(--ink-mut);text-decoration:line-through;font-weight:700;font-size:.62em;display:block;margin-bottom:2px;}
  .stat .cap{margin-top:10px;color:var(--ink-mut);font-size:14.5px;}
  .tbl-wrap{overflow-x:auto;border:1px solid var(--border);border-radius:16px;box-shadow:var(--shadow-sm);}
  table{border-collapse:collapse;width:100%;min-width:560px;background:var(--surface);}
  th,td{text-align:left;padding:14px 18px;font-size:15px;border-top:1px solid var(--border);}
  thead th{border-top:none;color:#fff;font-weight:700;}
  thead th:nth-child(1),thead th:nth-child(2){background:var(--ink);}
  thead th:nth-child(3){background:var(--accent1 );}
  tbody td:first-child{font-weight:700;}
  tbody td:nth-child(2){color:var(--ink-mut);}
  tbody td:nth-child(3){background:var(--accent-soft);font-weight:700;}
  .frame-line{margin-top:22px;color:var(--ink-mut);max-width:80ch;font-size:16px;}

  /* why it matters */
  .why{display:grid;grid-template-columns:repeat(3,1fr);gap:18px;}
  .tile{background:var(--surface);border:1px solid var(--border);border-radius:16px;padding:24px;box-shadow:var(--shadow-sm);}
  .tile .ic{background:var(--accent-soft);}
  .tile h3{color:var(--accent1);margin-bottom:6px;}
  .tile p{margin:0;color:var(--ink);font-size:15px;}

  /* final cta */
  .final{background:var(--ink);color:var(--canvas);border-radius:0;text-align:center;}
  .final h2{color:#fff;max-width:22ch;margin:0 auto 18px;}
  .final .lead{color:#cfc9c2;margin:0 auto 26px;}


  /* reveal animation */
  [data-reveal]{opacity:0;transform:translateY(18px);transition:opacity .6s ease, transform .6s ease;}
  [data-reveal].in{opacity:1;transform:none;}

  /* responsive */
  @media (max-width:900px){
    .cols-3,.board,.why,.stats{grid-template-columns:1fr;}
    .cols-2{grid-template-columns:1fr;}
    .map{grid-template-columns:1fr;}
    .map .mh.a{display:none;}
    .map .mrow.left{background:var(--canvas);}
  }
  @media (max-width:560px){
    section{padding:56px 0;}
    .step{grid-template-columns:1fr;}
    .step .num{margin-bottom:4px;}
  }
  @media (prefers-reduced-motion:reduce){
    html{scroll-behavior:auto;}
    [data-reveal]{opacity:1;transform:none;transition:none;}
    .btn:hover{transform:none;}
  }
`}</style>
      {/* ================= HERO ================= */}
      <section className="hero">
        <div className="wrap">
          <p className="eyebrow !text-[#2f378a]" data-reveal>
            Nexus for Oracle Fusion
          </p>
          <h1 data-reveal>
            Push Oracle Fusion config across every environment in one click.
          </h1>
          <p className="kicker !text-[#2f378a]" data-reveal>
            Tracked. Approved. Reversible.
          </p>
          <p className="lead" data-reveal>
            Nexus for Oracle Fusion discovers what changed, turns it into a
            reviewable changeset, promotes it through
            DEV&nbsp;→&nbsp;QA&nbsp;→&nbsp;UAT&nbsp;→&nbsp;Pre-Prod&nbsp;→&nbsp;Prod
            with the right approvals, writes it into Oracle for you, and rolls
            back in one click if anything breaks — all on an immutable audit
            trail.
          </p>
          <div className="cta-row" data-reveal>
            <a className="btn btn-primary" href="#cta">
              Book a 30-minute walkthrough
            </a>
            <a className="btn btn-ghost" href="#how">
              See how it works ↓
            </a>
          </div>
          <p className="trust" data-reveal>
            Built by Oracle Fusion experts at TechProjects · Runs on{" "}
            <b>Kubernetes</b> in your cloud (GCP, Azure, AWS, OCI) · Any RDBMS ·
            Credentials secured in Vault, never stored in the app
          </p>

          {/* EMBEDDED ANIMATED HERO (styles scoped under #nexusHero) */}
          <div className="hero-anim" data-reveal>
            <svg
              id="nexusHero"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 1200 560"
              role="img"
              aria-label="Nexus pipeline: Discover lists each configuration change as it is found, Review selects what to migrate and what to defer, Promote moves the release through an approval gate at every stage, Execute deploys to each environment as its gate approves, and Production confirms every environment is in sync."
            >
              <defs>
                <filter
                  id="nxSoft"
                  x="-30%"
                  y="-30%"
                  width="160%"
                  height="160%"
                >
                  <feDropShadow
                    dx="0"
                    dy="3"
                    stdDeviation="5"
                    floodColor="#0F172A"
                    floodOpacity="0.12"
                  />
                </filter>
                <filter
                  id="nxCard"
                  x="-20%"
                  y="-20%"
                  width="140%"
                  height="140%"
                >
                  <feDropShadow
                    dx="0"
                    dy="6"
                    stdDeviation="10"
                    floodColor="#0F172A"
                    floodOpacity="0.14"
                  />
                </filter>
              </defs>
              <style>{`#nexusHero .eyebrow{font:700 14px/1 Inter,Arial,sans-serif;fill:#2f378a;letter-spacing:3px;}
    #nexusHero .lbl{font:600 15px/1 Inter,Arial,sans-serif;fill:#0F172A;text-anchor:middle;}
    #nexusHero .ni{stroke:#fff;stroke-width:4;fill:none;stroke-linecap:round;stroke-linejoin:round;}
    #nexusHero .p-num{font:700 13px/1 Inter,Arial,sans-serif;fill:#fff;text-anchor:middle;}
    #nexusHero .p-name{font:800 16px/1 Inter,Arial,sans-serif;fill:#0F172A;}
    #nexusHero .p-sub{font:600 12px/1 Inter,Arial,sans-serif;fill:#434253;letter-spacing:.4px;}
    #nexusHero .badge-t{font:800 15px/1 Inter,Arial,sans-serif;text-anchor:middle;}
    #nexusHero .row-t{font:700 14px/1 Inter,Arial,sans-serif;fill:#0F172A;}
    #nexusHero .row-d{font:500 12px/1 Inter,Arial,sans-serif;fill:#434253;}
    #nexusHero .tag-t{font:700 11px/1 Inter,Arial,sans-serif;text-anchor:middle;}
    #nexusHero .foot{font:700 12px/1 Inter,Arial,sans-serif;fill:#434253;}
    #nexusHero .stn{font:700 11px/1 Inter,Arial,sans-serif;fill:#0F172A;text-anchor:middle;}
    #nexusHero .train-t{font:800 12px/1 Inter,Arial,sans-serif;fill:#fff;text-anchor:middle;}
    #nexusHero .env-t{font:600 13px/1 Inter,Arial,sans-serif;fill:#0F172A;}
    #nexusHero .env-tp{font:800 13px/1 Inter,Arial,sans-serif;fill:#2f378a;}
    #nexusHero .win-t{font:800 22px/1 Inter,Arial,sans-serif;fill:#0F172A;text-anchor:middle;}
    #nexusHero .win-s{font:500 14px/1 Inter,Arial,sans-serif;fill:#434253;text-anchor:middle;}
    #nexusHero .chip-t{font:700 12px/1 Inter,Arial,sans-serif;text-anchor:middle;}
    #nexusHero .scene{opacity:0;}
#nexusHero .sc1{animation:sc1 27.0s linear infinite;}
    #nexusHero .sc2{animation:sc2 27.0s linear infinite;}
    #nexusHero .sc3{animation:sc3 27.0s linear infinite;}
    #nexusHero .sc4{animation:sc4 27.0s linear infinite;}
    #nexusHero .sc5{animation:sc5 27.0s linear infinite;}
    #nexusHero .spineprog{animation:spineprog 27.0s linear infinite;transform-origin:left center;transform-box:fill-box;}
    #nexusHero .rg1{animation:rg1 27.0s linear infinite;transform-origin:center;transform-box:fill-box;}
    #nexusHero .rg2{animation:rg2 27.0s linear infinite;transform-origin:center;transform-box:fill-box;}
    #nexusHero .rg3{animation:rg3 27.0s linear infinite;transform-origin:center;transform-box:fill-box;}
    #nexusHero .rg4{animation:rg4 27.0s linear infinite;transform-origin:center;transform-box:fill-box;}
    #nexusHero .rg5{animation:rg5 27.0s linear infinite;transform-origin:center;transform-box:fill-box;}
    #nexusHero .nb1{animation:nb1 27.0s linear infinite;transform-origin:center;transform-box:fill-box;}
    #nexusHero .nb2{animation:nb2 27.0s linear infinite;transform-origin:center;transform-box:fill-box;}
    #nexusHero .nb3{animation:nb3 27.0s linear infinite;transform-origin:center;transform-box:fill-box;}
    #nexusHero .nb4{animation:nb4 27.0s linear infinite;transform-origin:center;transform-box:fill-box;}
    #nexusHero .nb5{animation:nb5 27.0s linear infinite;transform-origin:center;transform-box:fill-box;}
    #nexusHero .d1{animation:d1 27.0s linear infinite;}
    #nexusHero .d2{animation:d2 27.0s linear infinite;}
    #nexusHero .d3{animation:d3 27.0s linear infinite;}
    #nexusHero .d4{animation:d4 27.0s linear infinite;}
    #nexusHero .v1{animation:v1 27.0s linear infinite;}
    #nexusHero .vr1{animation:vr1 27.0s linear infinite;transform-origin:center;transform-box:fill-box;}
    #nexusHero .vc1{animation:vc1 27.0s linear infinite;transform-origin:center;transform-box:fill-box;}
    #nexusHero .v2{animation:v2 27.0s linear infinite;}
    #nexusHero .vr2{animation:vr2 27.0s linear infinite;transform-origin:center;transform-box:fill-box;}
    #nexusHero .vc2{animation:vc2 27.0s linear infinite;transform-origin:center;transform-box:fill-box;}
    #nexusHero .v3{animation:v3 27.0s linear infinite;}
    #nexusHero .vr3{animation:vr3 27.0s linear infinite;transform-origin:center;transform-box:fill-box;}
    #nexusHero .vc3{animation:vc3 27.0s linear infinite;transform-origin:center;transform-box:fill-box;}
    #nexusHero .vcur{animation:vcur 27.0s linear infinite;}
    #nexusHero .v4{animation:v4 27.0s linear infinite;}
    #nexusHero .p0{animation:p0 27.0s linear infinite;}
    #nexusHero .ptrack{animation:ptrack 27.0s linear infinite;transform-origin:left center;transform-box:fill-box;}
    #nexusHero .pa1{animation:pa1 27.0s linear infinite;transform-origin:center;transform-box:fill-box;}
    #nexusHero .pr1{animation:pr1 27.0s linear infinite;transform-origin:center;transform-box:fill-box;}
    #nexusHero .pa2{animation:pa2 27.0s linear infinite;transform-origin:center;transform-box:fill-box;}
    #nexusHero .pr2{animation:pr2 27.0s linear infinite;transform-origin:center;transform-box:fill-box;}
    #nexusHero .pa3{animation:pa3 27.0s linear infinite;transform-origin:center;transform-box:fill-box;}
    #nexusHero .pr3{animation:pr3 27.0s linear infinite;transform-origin:center;transform-box:fill-box;}
    #nexusHero .pa4{animation:pa4 27.0s linear infinite;transform-origin:center;transform-box:fill-box;}
    #nexusHero .pr4{animation:pr4 27.0s linear infinite;transform-origin:center;transform-box:fill-box;}
    #nexusHero .pa5{animation:pa5 27.0s linear infinite;transform-origin:center;transform-box:fill-box;}
    #nexusHero .pr5{animation:pr5 27.0s linear infinite;transform-origin:center;transform-box:fill-box;}
    #nexusHero .ptrain{animation:ptrain 27.0s linear infinite;}
    #nexusHero .p9{animation:p9 27.0s linear infinite;}
    #nexusHero .e0{animation:e0 27.0s linear infinite;}
    #nexusHero .eb1{animation:eb1 27.0s linear infinite;transform-origin:left center;transform-box:fill-box;}
    #nexusHero .et1{animation:et1 27.0s linear infinite;transform-origin:center;transform-box:fill-box;}
    #nexusHero .eb2{animation:eb2 27.0s linear infinite;transform-origin:left center;transform-box:fill-box;}
    #nexusHero .et2{animation:et2 27.0s linear infinite;transform-origin:center;transform-box:fill-box;}
    #nexusHero .eb3{animation:eb3 27.0s linear infinite;transform-origin:left center;transform-box:fill-box;}
    #nexusHero .et3{animation:et3 27.0s linear infinite;transform-origin:center;transform-box:fill-box;}
    #nexusHero .eb4{animation:eb4 27.0s linear infinite;transform-origin:left center;transform-box:fill-box;}
    #nexusHero .et4{animation:et4 27.0s linear infinite;transform-origin:center;transform-box:fill-box;}
    #nexusHero .eb5{animation:eb5 27.0s linear infinite;transform-origin:left center;transform-box:fill-box;}
    #nexusHero .et5{animation:et5 27.0s linear infinite;transform-origin:center;transform-box:fill-box;}
    #nexusHero .e9{animation:e9 27.0s linear infinite;}
    #nexusHero .f1{animation:f1 27.0s linear infinite;transform-origin:center;transform-box:fill-box;}
    #nexusHero .f1r{animation:f1r 27.0s linear infinite;transform-origin:center;transform-box:fill-box;}
    #nexusHero .sp1{animation:sp1 27.0s linear infinite;transform-origin:center;transform-box:fill-box;}
    #nexusHero .sp2{animation:sp2 27.0s linear infinite;transform-origin:center;transform-box:fill-box;}
    #nexusHero .sp3{animation:sp3 27.0s linear infinite;transform-origin:center;transform-box:fill-box;}
    #nexusHero .sp4{animation:sp4 27.0s linear infinite;transform-origin:center;transform-box:fill-box;}
    #nexusHero .f2{animation:f2 27.0s linear infinite;}
    #nexusHero .f3{animation:f3 27.0s linear infinite;}
    #nexusHero .ch1{animation:ch1 27.0s linear infinite;}
    #nexusHero .ch2{animation:ch2 27.0s linear infinite;}
    #nexusHero .ch3{animation:ch3 27.0s linear infinite;}
    #nexusHero .f4{animation:f4 27.0s linear infinite;}
    @keyframes sc1{0%,1.481%{opacity:0;transform:translateY(10px)}3.148%{opacity:1;transform:translateY(0)}18.519%{opacity:1;transform:translateY(0)}20.0%{opacity:0;transform:translateY(-6px)}100%{opacity:0}}
    @keyframes sc2{0%,20.0%{opacity:0;transform:translateY(10px)}21.667%{opacity:1;transform:translateY(0)}38.519%{opacity:1;transform:translateY(0)}40.0%{opacity:0;transform:translateY(-6px)}100%{opacity:0}}
    @keyframes sc3{0%,40.0%{opacity:0;transform:translateY(10px)}41.667%{opacity:1;transform:translateY(0)}60.0%{opacity:1;transform:translateY(0)}61.481%{opacity:0;transform:translateY(-6px)}100%{opacity:0}}
    @keyframes sc4{0%,61.481%{opacity:0;transform:translateY(10px)}63.148%{opacity:1;transform:translateY(0)}82.222%{opacity:1;transform:translateY(0)}83.704%{opacity:0;transform:translateY(-6px)}100%{opacity:0}}
    @keyframes sc5{0%,83.704%{opacity:0;transform:translateY(10px)}85.37%{opacity:1;transform:translateY(0)}98.519%{opacity:1;transform:translateY(0)}100.0%{opacity:0;transform:translateY(-6px)}100%{opacity:0}}
    @keyframes spineprog{0%{transform:scaleX(0)}19.259%{transform:scaleX(0)}21.111%{transform:scaleX(0.25)}39.259%{transform:scaleX(0.25)}41.111%{transform:scaleX(0.5)}60.741%{transform:scaleX(0.5)}62.593%{transform:scaleX(0.75)}82.963%{transform:scaleX(0.75)}84.815%{transform:scaleX(1.0)}97.407%{transform:scaleX(1.0)}100%{transform:scaleX(1.0)}}
    @keyframes rg1{0%,1.481%{opacity:0;transform:scale(.9)}2.963%{opacity:.30;transform:scale(1.2)}18.519%{opacity:.30;transform:scale(1.2)}19.815%{opacity:0;transform:scale(1.32)}100%{opacity:0}}
    @keyframes rg2{0%,20.0%{opacity:0;transform:scale(.9)}21.481%{opacity:.30;transform:scale(1.2)}38.519%{opacity:.30;transform:scale(1.2)}39.815%{opacity:0;transform:scale(1.32)}100%{opacity:0}}
    @keyframes rg3{0%,40.0%{opacity:0;transform:scale(.9)}41.481%{opacity:.30;transform:scale(1.2)}60.0%{opacity:.30;transform:scale(1.2)}61.296%{opacity:0;transform:scale(1.32)}100%{opacity:0}}
    @keyframes rg4{0%,61.481%{opacity:0;transform:scale(.9)}62.963%{opacity:.30;transform:scale(1.2)}82.222%{opacity:.30;transform:scale(1.2)}83.519%{opacity:0;transform:scale(1.32)}100%{opacity:0}}
    @keyframes rg5{0%,83.704%{opacity:0;transform:scale(.9)}85.185%{opacity:.30;transform:scale(1.2)}98.519%{opacity:.30;transform:scale(1.2)}99.815%{opacity:0;transform:scale(1.32)}100%{opacity:0}}
    @keyframes nb1{0%,17.037%{opacity:0;transform:scale(.35)}17.815%{opacity:1;transform:scale(1.18)}18.148%{opacity:1;transform:scale(1)}97.407%{opacity:1;transform:scale(1)}98.519%{opacity:0;transform:scale(1)}100%{opacity:0}}
    @keyframes nb2{0%,37.037%{opacity:0;transform:scale(.35)}37.815%{opacity:1;transform:scale(1.18)}38.148%{opacity:1;transform:scale(1)}97.407%{opacity:1;transform:scale(1)}98.519%{opacity:0;transform:scale(1)}100%{opacity:0}}
    @keyframes nb3{0%,58.519%{opacity:0;transform:scale(.35)}59.296%{opacity:1;transform:scale(1.18)}59.63%{opacity:1;transform:scale(1)}97.407%{opacity:1;transform:scale(1)}98.519%{opacity:0;transform:scale(1)}100%{opacity:0}}
    @keyframes nb4{0%,80.741%{opacity:0;transform:scale(.35)}81.519%{opacity:1;transform:scale(1.18)}81.852%{opacity:1;transform:scale(1)}97.407%{opacity:1;transform:scale(1)}98.519%{opacity:0;transform:scale(1)}100%{opacity:0}}
    @keyframes nb5{0%,85.185%{opacity:0;transform:scale(.35)}85.963%{opacity:1;transform:scale(1.18)}86.296%{opacity:1;transform:scale(1)}97.407%{opacity:1;transform:scale(1)}98.519%{opacity:0;transform:scale(1)}100%{opacity:0}}
    @keyframes d1{0%,3.704%{opacity:0;transform:translateY(7px)}5.259%{opacity:1;transform:translateY(0)}100%{opacity:1;transform:translateY(0)}}
    @keyframes d2{0%,7.037%{opacity:0;transform:translateY(7px)}8.593%{opacity:1;transform:translateY(0)}100%{opacity:1;transform:translateY(0)}}
    @keyframes d3{0%,10.37%{opacity:0;transform:translateY(7px)}11.926%{opacity:1;transform:translateY(0)}100%{opacity:1;transform:translateY(0)}}
    @keyframes d4{0%,13.704%{opacity:0;transform:translateY(7px)}15.259%{opacity:1;transform:translateY(0)}100%{opacity:1;transform:translateY(0)}}
    @keyframes v1{0%,21.852%{opacity:0;transform:translateY(7px)}23.407%{opacity:1;transform:translateY(0)}100%{opacity:1;transform:translateY(0)}}
    @keyframes vr1{0%,25.852%{opacity:.55;transform:scale(.3)}27.704%{opacity:0;transform:scale(2.1)}100%{opacity:0;transform:scale(2.1)}}
    @keyframes vc1{0%,26.296%{opacity:0;transform:scale(.35)}27.074%{opacity:1;transform:scale(1.18)}27.407%{opacity:1;transform:scale(1)}100%{opacity:1;transform:scale(1)}}
    @keyframes v2{0%,22.593%{opacity:0;transform:translateY(7px)}24.148%{opacity:1;transform:translateY(0)}100%{opacity:1;transform:translateY(0)}}
    @keyframes vr2{0%,29.185%{opacity:.55;transform:scale(.3)}31.037%{opacity:0;transform:scale(2.1)}100%{opacity:0;transform:scale(2.1)}}
    @keyframes vc2{0%,29.63%{opacity:0;transform:scale(.35)}30.407%{opacity:1;transform:scale(1.18)}30.741%{opacity:1;transform:scale(1)}100%{opacity:1;transform:scale(1)}}
    @keyframes v3{0%,23.333%{opacity:0;transform:translateY(7px)}24.889%{opacity:1;transform:translateY(0)}100%{opacity:1;transform:translateY(0)}}
    @keyframes vr3{0%,32.519%{opacity:.55;transform:scale(.3)}34.37%{opacity:0;transform:scale(2.1)}100%{opacity:0;transform:scale(2.1)}}
    @keyframes vc3{0%,32.963%{opacity:0;transform:scale(.35)}33.741%{opacity:1;transform:scale(1.18)}34.074%{opacity:1;transform:scale(1)}100%{opacity:1;transform:scale(1)}}
    @keyframes vcur{0%,24.259%{opacity:0;transform:translateX(0px)}24.815%{opacity:1;transform:translateX(0px)}27.407%{transform:translateX(0px)}28.519%{transform:translateX(60px)}30.741%{transform:translateX(60px)}31.852%{transform:translateX(120px)}35.556%{transform:translateX(120px)}35.926%{opacity:1;transform:translateX(120px)}37.037%{opacity:0}100%{opacity:0;transform:translateX(120px)}}
    @keyframes v4{0%,34.815%{opacity:0;transform:translateY(7px)}36.37%{opacity:1;transform:translateY(0)}100%{opacity:1;transform:translateY(0)}}
    @keyframes p0{0%,41.481%{opacity:0;transform:translateY(7px)}43.037%{opacity:1;transform:translateY(0)}100%{opacity:1;transform:translateY(0)}}
    @keyframes ptrack{0%{transform:scaleX(0)}45.185%{transform:scaleX(0)}46.667%{transform:scaleX(0.25)}48.519%{transform:scaleX(0.25)}50.0%{transform:scaleX(0.5)}51.852%{transform:scaleX(0.5)}53.333%{transform:scaleX(0.75)}55.185%{transform:scaleX(0.75)}56.667%{transform:scaleX(1.0)}100%{transform:scaleX(1.0)}}
    @keyframes pa1{0%,44.074%{opacity:0;transform:scale(.35)}44.852%{opacity:1;transform:scale(1.18)}45.185%{opacity:1;transform:scale(1)}100%{opacity:1;transform:scale(1)}}
    @keyframes pr1{0%,43.704%{opacity:.55;transform:scale(.3)}45.556%{opacity:0;transform:scale(2.1)}100%{opacity:0;transform:scale(2.1)}}
    @keyframes pa2{0%,47.407%{opacity:0;transform:scale(.35)}48.185%{opacity:1;transform:scale(1.18)}48.519%{opacity:1;transform:scale(1)}100%{opacity:1;transform:scale(1)}}
    @keyframes pr2{0%,47.037%{opacity:.55;transform:scale(.3)}48.889%{opacity:0;transform:scale(2.1)}100%{opacity:0;transform:scale(2.1)}}
    @keyframes pa3{0%,50.741%{opacity:0;transform:scale(.35)}51.519%{opacity:1;transform:scale(1.18)}51.852%{opacity:1;transform:scale(1)}100%{opacity:1;transform:scale(1)}}
    @keyframes pr3{0%,50.37%{opacity:.55;transform:scale(.3)}52.222%{opacity:0;transform:scale(2.1)}100%{opacity:0;transform:scale(2.1)}}
    @keyframes pa4{0%,54.074%{opacity:0;transform:scale(.35)}54.852%{opacity:1;transform:scale(1.18)}55.185%{opacity:1;transform:scale(1)}100%{opacity:1;transform:scale(1)}}
    @keyframes pr4{0%,53.704%{opacity:.55;transform:scale(.3)}55.556%{opacity:0;transform:scale(2.1)}100%{opacity:0;transform:scale(2.1)}}
    @keyframes pa5{0%,57.407%{opacity:0;transform:scale(.35)}58.185%{opacity:1;transform:scale(1.18)}58.519%{opacity:1;transform:scale(1)}100%{opacity:1;transform:scale(1)}}
    @keyframes pr5{0%,57.037%{opacity:.55;transform:scale(.3)}58.889%{opacity:0;transform:scale(2.1)}100%{opacity:0;transform:scale(2.1)}}
    @keyframes ptrain{0%,42.593%{opacity:0;transform:translateX(0px)}43.519%{opacity:1;transform:translateX(0px)}45.185%{transform:translateX(0px)}46.667%{transform:translateX(80px)}48.519%{transform:translateX(80px)}50.0%{transform:translateX(160px)}51.852%{transform:translateX(160px)}53.333%{transform:translateX(240px)}55.185%{transform:translateX(240px)}56.667%{transform:translateX(320px)}100%{transform:translateX(320px)}}
    @keyframes p9{0%,58.519%{opacity:0;transform:translateY(7px)}60.074%{opacity:1;transform:translateY(0)}100%{opacity:1;transform:translateY(0)}}
    @keyframes e0{0%,62.963%{opacity:0;transform:translateY(7px)}64.519%{opacity:1;transform:translateY(0)}100%{opacity:1;transform:translateY(0)}}
    @keyframes eb1{0%,64.815%{transform:scaleX(0)}66.296%{transform:scaleX(1)}100%{transform:scaleX(1)}}
    @keyframes et1{0%,66.481%{opacity:0;transform:scale(.35)}67.259%{opacity:1;transform:scale(1.18)}67.593%{opacity:1;transform:scale(1)}100%{opacity:1;transform:scale(1)}}
    @keyframes eb2{0%,68.148%{transform:scaleX(0)}69.63%{transform:scaleX(1)}100%{transform:scaleX(1)}}
    @keyframes et2{0%,69.815%{opacity:0;transform:scale(.35)}70.593%{opacity:1;transform:scale(1.18)}70.926%{opacity:1;transform:scale(1)}100%{opacity:1;transform:scale(1)}}
    @keyframes eb3{0%,71.481%{transform:scaleX(0)}72.963%{transform:scaleX(1)}100%{transform:scaleX(1)}}
    @keyframes et3{0%,73.148%{opacity:0;transform:scale(.35)}73.926%{opacity:1;transform:scale(1.18)}74.259%{opacity:1;transform:scale(1)}100%{opacity:1;transform:scale(1)}}
    @keyframes eb4{0%,74.815%{transform:scaleX(0)}76.296%{transform:scaleX(1)}100%{transform:scaleX(1)}}
    @keyframes et4{0%,76.481%{opacity:0;transform:scale(.35)}77.259%{opacity:1;transform:scale(1.18)}77.593%{opacity:1;transform:scale(1)}100%{opacity:1;transform:scale(1)}}
    @keyframes eb5{0%,78.148%{transform:scaleX(0)}79.63%{transform:scaleX(1)}100%{transform:scaleX(1)}}
    @keyframes et5{0%,79.815%{opacity:0;transform:scale(.35)}80.593%{opacity:1;transform:scale(1.18)}80.926%{opacity:1;transform:scale(1)}100%{opacity:1;transform:scale(1)}}
    @keyframes e9{0%,80.741%{opacity:0;transform:translateY(7px)}82.296%{opacity:1;transform:translateY(0)}100%{opacity:1;transform:translateY(0)}}
    @keyframes f1{0%,84.815%{opacity:0;transform:scale(.35)}85.981%{opacity:1;transform:scale(1.18)}86.481%{opacity:1;transform:scale(1)}100%{opacity:1;transform:scale(1)}}
    @keyframes f1r{0%,85.0%{opacity:.55;transform:scale(.3)}87.593%{opacity:0;transform:scale(2.1)}100%{opacity:0;transform:scale(2.1)}}
    @keyframes sp1{0%,85.815%{opacity:0;transform:scale(.35)}86.722%{opacity:1;transform:scale(1.18)}87.111%{opacity:1;transform:scale(1)}100%{opacity:1;transform:scale(1)}}
    @keyframes sp2{0%,86.074%{opacity:0;transform:scale(.35)}86.981%{opacity:1;transform:scale(1.18)}87.37%{opacity:1;transform:scale(1)}100%{opacity:1;transform:scale(1)}}
    @keyframes sp3{0%,86.333%{opacity:0;transform:scale(.35)}87.241%{opacity:1;transform:scale(1.18)}87.63%{opacity:1;transform:scale(1)}100%{opacity:1;transform:scale(1)}}
    @keyframes sp4{0%,86.593%{opacity:0;transform:scale(.35)}87.5%{opacity:1;transform:scale(1.18)}87.889%{opacity:1;transform:scale(1)}100%{opacity:1;transform:scale(1)}}
    @keyframes f2{0%,86.481%{opacity:0;transform:translateY(7px)}88.037%{opacity:1;transform:translateY(0)}100%{opacity:1;transform:translateY(0)}}
    @keyframes f3{0%,87.222%{opacity:0;transform:translateY(7px)}88.778%{opacity:1;transform:translateY(0)}100%{opacity:1;transform:translateY(0)}}
    @keyframes ch1{0%,88.333%{opacity:0;transform:translateY(7px)}89.889%{opacity:1;transform:translateY(0)}100%{opacity:1;transform:translateY(0)}}
    @keyframes ch2{0%,89.074%{opacity:0;transform:translateY(7px)}90.63%{opacity:1;transform:translateY(0)}100%{opacity:1;transform:translateY(0)}}
    @keyframes ch3{0%,89.815%{opacity:0;transform:translateY(7px)}91.37%{opacity:1;transform:translateY(0)}100%{opacity:1;transform:translateY(0)}}
    @keyframes f4{0%,91.111%{opacity:0;transform:translateY(7px)}92.667%{opacity:1;transform:translateY(0)}100%{opacity:1;transform:translateY(0)}}
    @media (prefers-reduced-motion:reduce){
      #nexusHero *{animation:none !important;}
      #nexusHero .scene{opacity:0;}
      #nexusHero .sc1{opacity:1;}
      #nexusHero .d1,#nexusHero .d2,#nexusHero .d3,#nexusHero .d4{opacity:1;transform:none;}
      #nexusHero .spineprog{transform:scaleX(0);}
      #nexusHero .nb1,#nexusHero .nb2,#nexusHero .nb3,#nexusHero .nb4,#nexusHero .nb5{opacity:0;}
    }`}</style>
              <rect
                x="0"
                y="0"
                width="1200"
                height="560"
                rx="20"
                fill="#F8FAFC"
              />
              <text className="eyebrow !text-[#2f378a]" x="60" y="40">
                ONE GOVERNED PIPELINE
              </text>
              <rect
                x="150"
                y="467"
                width="900"
                height="6"
                rx="3"
                fill="#E2E8F0"
              />
              <rect
                className="spineprog"
                x="150"
                y="467"
                width="900"
                height="6"
                rx="3"
                fill="#2f378a"
              />
              <circle className="rg1" cx="150" cy="470" r="30" fill="#2f378a" />
              <circle className="rg2" cx="375" cy="470" r="30" fill="#2f378a" />
              <circle className="rg3" cx="600" cy="470" r="30" fill="#2f378a" />
              <circle className="rg4" cx="825" cy="470" r="30" fill="#2f378a" />
              <circle
                className="rg5"
                cx="1050"
                cy="470"
                r="30"
                fill="#2f378a"
              />
              <g filter="url(#nxSoft)">
                <circle cx="150" cy="470" r="30" fill="#2f378a" />
              </g>
              <circle cx="145" cy="465" r="9" className="ni" />
              <line x1="151" y1="471" x2="160" y2="480" className="ni" />
              <text className="lbl" x="150" y="516">
                Discover
              </text>
              <g filter="url(#nxSoft)">
                <circle cx="375" cy="470" r="30" fill="#2f378a" />
              </g>
              <polyline points="364,470 372,478 388,460" className="ni" />
              <text className="lbl" x="375" y="516">
                Review
              </text>
              <g filter="url(#nxSoft)">
                <circle cx="600" cy="470" r="30" fill="#2f378a" />
              </g>
              <polyline points="591,461 600,470 591,479" className="ni" />
              <polyline points="603,461 612,470 603,479" className="ni" />
              <text className="lbl" x="600" y="516">
                Promote
              </text>
              <g filter="url(#nxSoft)">
                <circle cx="825" cy="470" r="30" fill="#2f378a" />
              </g>
              <path
                d="M828 446 L815 464 L823 464 L820 476 L835 459 L826 459 Z"
                fill="#fff"
              />
              <text className="lbl" x="825" y="516">
                Execute
              </text>
              <g filter="url(#nxSoft)">
                <circle cx="1050" cy="470" r="30" fill="#2f378a" />
              </g>
              <path
                d="M1050 447 L1039 461 L1046 461 L1046 474 L1054 474 L1054 461 L1061 461 Z"
                fill="#fff"
              />
              <text className="lbl" x="1050" y="516">
                Production
              </text>
              <g className="nb1">
                <circle
                  cx="173"
                  cy="447"
                  r="11"
                  fill="#2f378a"
                  stroke="#fff"
                  strokeWidth="2.4"
                />
                <polyline
                  points="168,447 172,451 178.5,442"
                  fill="none"
                  stroke="#fff"
                  strokeWidth="2.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </g>
              <g className="nb2">
                <circle
                  cx="398"
                  cy="447"
                  r="11"
                  fill="#2f378a"
                  stroke="#fff"
                  strokeWidth="2.4"
                />
                <polyline
                  points="393,447 397,451 403.5,442"
                  fill="none"
                  stroke="#fff"
                  strokeWidth="2.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </g>
              <g className="nb3">
                <circle
                  cx="623"
                  cy="447"
                  r="11"
                  fill="#2f378a"
                  stroke="#fff"
                  strokeWidth="2.4"
                />
                <polyline
                  points="618,447 622,451 628.5,442"
                  fill="none"
                  stroke="#fff"
                  strokeWidth="2.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </g>
              <g className="nb4">
                <circle
                  cx="848"
                  cy="447"
                  r="11"
                  fill="#2f378a"
                  stroke="#fff"
                  strokeWidth="2.4"
                />
                <polyline
                  points="843,447 847,451 853.5,442"
                  fill="none"
                  stroke="#fff"
                  strokeWidth="2.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </g>
              <g className="nb5">
                <circle
                  cx="1073"
                  cy="447"
                  r="13"
                  fill="#2f378a"
                  stroke="#fff"
                  strokeWidth="2.4"
                />
                <polyline
                  points="1068,447 1072,451 1078.5,442"
                  fill="none"
                  stroke="#fff"
                  strokeWidth="2.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </g>
              <g className="scene sc1">
                <line
                  x1="150"
                  y1="380"
                  x2="150"
                  y2="440"
                  stroke="#2f378a"
                  strokeWidth="2"
                  strokeDasharray="3 4"
                />
                <g filter="url(#nxCard)">
                  <rect
                    x="40"
                    y="60"
                    width="420"
                    height="320"
                    rx="16"
                    fill="#FFFFFF"
                    stroke="#E2E8F0"
                  />
                </g>
                <circle cx="74" cy="92" r="13" fill="#2f378a" />
                <text className="p-num" x="74" y="97">
                  1
                </text>
                <text className="p-name" x="96" y="97">
                  Discover
                </text>
                <text className="p-sub" x="64" y="120">
                  CHANGES FOUND SINCE BASELINE
                </text>
                <line x1="60" y1="134" x2="440" y2="134" stroke="#E2E8F0" />
                <g className="d1">
                  <rect
                    x="64"
                    y="152"
                    width="30"
                    height="24"
                    rx="7"
                    fill="#EAF0FF"
                  />
                  <text className="badge-t" x="79" y="169" fill="#2f378a">
                    +
                  </text>
                  <text className="row-t" x="106" y="164">
                    PIM Item
                  </text>
                  <text className="row-d" x="106" y="181">
                    SKU-4471 &#183; Laptop Stand
                  </text>
                </g>
                <g className="d2">
                  <rect
                    x="64"
                    y="212"
                    width="30"
                    height="24"
                    rx="7"
                    fill="#F3E9D7"
                  />
                  <text className="badge-t" x="79" y="229" fill="#B8843A">
                    ~
                  </text>
                  <text className="row-t" x="106" y="224">
                    Price List
                  </text>
                  <text className="row-d" x="106" y="241">
                    NA-RETAIL &#183; +5% tier
                  </text>
                </g>
                <g className="d3">
                  <rect
                    x="64"
                    y="272"
                    width="30"
                    height="24"
                    rx="7"
                    fill="#EAF0FF"
                  />
                  <text className="badge-t" x="79" y="289" fill="#2f378a">
                    +
                  </text>
                  <text className="row-t" x="106" y="284">
                    Charge
                  </text>
                  <text className="row-d" x="106" y="301">
                    Freight Surcharge
                  </text>
                </g>
                <text className="foot d4" x="64" y="345">
                  3 changes found
                </text>
              </g>
              <g className="scene sc2">
                <line
                  x1="375"
                  y1="380"
                  x2="375"
                  y2="440"
                  stroke="#2f378a"
                  strokeWidth="2"
                  strokeDasharray="3 4"
                />
                <g filter="url(#nxCard)">
                  <rect
                    x="175"
                    y="60"
                    width="420"
                    height="320"
                    rx="16"
                    fill="#FFFFFF"
                    stroke="#E2E8F0"
                  />
                </g>
                <circle cx="209" cy="92" r="13" fill="#2f378a" />
                <text className="p-num" x="209" y="97">
                  2
                </text>
                <text className="p-name" x="231" y="97">
                  Review
                </text>
                <text className="p-sub" x="199" y="120">
                  SELECT WHAT TO MIGRATE
                </text>
                <line x1="195" y1="134" x2="575" y2="134" stroke="#E2E8F0" />
                <g className="v1">
                  <rect
                    x="199"
                    y="152"
                    width="22"
                    height="22"
                    rx="6"
                    fill="#fff"
                    stroke="#CBD5E1"
                    strokeWidth="2"
                  />
                  <text className="row-t" x="233" y="168">
                    PIM Item &#8212; SKU-4471
                  </text>
                </g>
                <circle
                  className="vr1"
                  cx="210"
                  cy="163"
                  r="14"
                  fill="#2f378a"
                />
                <g className="vc1">
                  <rect
                    x="199"
                    y="152"
                    width="22"
                    height="22"
                    rx="6"
                    fill="#2f378a"
                  />
                  <polyline
                    points="204,163 209,168 216,157"
                    fill="none"
                    stroke="#fff"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <rect
                    x="461"
                    y="153"
                    width="64"
                    height="21"
                    rx="10"
                    fill="#EAF0FF"
                  />
                  <text className="tag-t" x="493" y="167" fill="#2f378a">
                    Migrate
                  </text>
                </g>
                <g className="v2">
                  <rect
                    x="199"
                    y="212"
                    width="22"
                    height="22"
                    rx="6"
                    fill="#fff"
                    stroke="#CBD5E1"
                    strokeWidth="2"
                  />
                  <text className="row-t" x="233" y="228">
                    Charge &#8212; Freight Surcharge
                  </text>
                </g>
                <circle
                  className="vr2"
                  cx="210"
                  cy="223"
                  r="14"
                  fill="#2f378a"
                />
                <g className="vc2">
                  <rect
                    x="199"
                    y="212"
                    width="22"
                    height="22"
                    rx="6"
                    fill="#2f378a"
                  />
                  <polyline
                    points="204,223 209,228 216,217"
                    fill="none"
                    stroke="#fff"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <rect
                    x="461"
                    y="213"
                    width="64"
                    height="21"
                    rx="10"
                    fill="#EAF0FF"
                  />
                  <text className="tag-t" x="493" y="227" fill="#2f378a">
                    Migrate
                  </text>
                </g>
                <g className="v3">
                  <rect
                    x="199"
                    y="272"
                    width="22"
                    height="22"
                    rx="6"
                    fill="#fff"
                    stroke="#CBD5E1"
                    strokeWidth="2"
                  />
                  <text className="row-t" x="233" y="288" fill="#94A3B8">
                    Price List &#8212; NA-RETAIL
                  </text>
                </g>
                <circle
                  className="vr3"
                  cx="210"
                  cy="283"
                  r="14"
                  fill="#F42F39"
                />
                <g className="vc3">
                  <rect
                    x="441"
                    y="273"
                    width="84"
                    height="21"
                    rx="10"
                    fill="#FDDFDF"
                  />
                  <text className="tag-t" x="483" y="287" fill="#F42F39">
                    Defer to next
                  </text>
                </g>
                <g className="vcur">
                  <path
                    d="M213 158 l0 18 l4.6 -4.6 l3.1 6.6 l3.4 -1.6 l-3.0 -6.4 l6.3 -0.4 Z"
                    fill="#0F172A"
                    stroke="#fff"
                    strokeWidth="1.4"
                    strokeLinejoin="round"
                  />
                </g>
                <text className="foot v4" x="199" y="345">
                  2 to migrate &#183; 1 deferred
                </text>
              </g>
              <g className="scene sc3">
                <line
                  x1="600"
                  y1="380"
                  x2="600"
                  y2="440"
                  stroke="#2f378a"
                  strokeWidth="2"
                  strokeDasharray="3 4"
                />
                <g filter="url(#nxCard)">
                  <rect
                    x="390"
                    y="60"
                    width="420"
                    height="320"
                    rx="16"
                    fill="#FFFFFF"
                    stroke="#E2E8F0"
                  />
                </g>
                <circle cx="424" cy="92" r="13" fill="#2f378a" />
                <text className="p-num" x="424" y="97">
                  3
                </text>
                <text className="p-name" x="446" y="97">
                  Promote
                </text>
                <text className="p-sub" x="414" y="120">
                  RELEASE PIPELINE &#183; APPROVAL AT EACH GATE
                </text>
                <line x1="410" y1="134" x2="790" y2="134" stroke="#E2E8F0" />
                <g className="p0">
                  <rect
                    x="440"
                    y="272"
                    width="320"
                    height="6"
                    rx="3"
                    fill="#E2E8F0"
                  />
                  <circle
                    cx="440"
                    cy="275"
                    r="13"
                    fill="#fff"
                    stroke="#CBD5E1"
                    strokeWidth="2.5"
                  />
                  <text className="stn" x="440" y="308">
                    DEV
                  </text>
                  <circle
                    cx="520"
                    cy="275"
                    r="13"
                    fill="#fff"
                    stroke="#CBD5E1"
                    strokeWidth="2.5"
                  />
                  <text className="stn" x="520" y="308">
                    QA
                  </text>
                  <circle
                    cx="600"
                    cy="275"
                    r="13"
                    fill="#fff"
                    stroke="#CBD5E1"
                    strokeWidth="2.5"
                  />
                  <text className="stn" x="600" y="308">
                    UAT
                  </text>
                  <circle
                    cx="680"
                    cy="275"
                    r="13"
                    fill="#fff"
                    stroke="#CBD5E1"
                    strokeWidth="2.5"
                  />
                  <text className="stn" x="680" y="308">
                    P-PROD
                  </text>
                  <circle
                    cx="760"
                    cy="275"
                    r="13"
                    fill="#fff"
                    stroke="#CBD5E1"
                    strokeWidth="2.5"
                  />
                  <text className="stn" x="760" y="308">
                    PROD
                  </text>
                </g>
                <rect
                  className="ptrack"
                  x="440"
                  y="272"
                  width="320"
                  height="6"
                  rx="3"
                  fill="#2f378a"
                />
                <g className="pa1">
                  <circle cx="440" cy="275" r="13" fill="#2f378a" />
                  <polyline
                    points="434.5,275 439,279.5 446,269"
                    fill="none"
                    stroke="#fff"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </g>
                <circle
                  className="pr1"
                  cx="440"
                  cy="275"
                  r="15"
                  fill="#2f378a"
                />
                <g className="pa2">
                  <circle cx="520" cy="275" r="13" fill="#2f378a" />
                  <polyline
                    points="514.5,275 519,279.5 526,269"
                    fill="none"
                    stroke="#fff"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </g>
                <circle
                  className="pr2"
                  cx="520"
                  cy="275"
                  r="15"
                  fill="#2f378a"
                />
                <g className="pa3">
                  <circle cx="600" cy="275" r="13" fill="#2f378a" />
                  <polyline
                    points="594.5,275 599,279.5 606,269"
                    fill="none"
                    stroke="#fff"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </g>
                <circle
                  className="pr3"
                  cx="600"
                  cy="275"
                  r="15"
                  fill="#2f378a"
                />
                <g className="pa4">
                  <circle cx="680" cy="275" r="13" fill="#2f378a" />
                  <polyline
                    points="674.5,275 679,279.5 686,269"
                    fill="none"
                    stroke="#fff"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </g>
                <circle
                  className="pr4"
                  cx="680"
                  cy="275"
                  r="15"
                  fill="#2f378a"
                />
                <g className="pa5">
                  <circle cx="760" cy="275" r="13" fill="#2f378a" />
                  <polyline
                    points="754.5,275 759,279.5 766,269"
                    fill="none"
                    stroke="#fff"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </g>
                <circle
                  className="pr5"
                  cx="760"
                  cy="275"
                  r="15"
                  fill="#2f378a"
                />
                <g className="ptrain">
                  <g filter="url(#nxCard)">
                    <rect
                      x="405"
                      y="200"
                      width="70"
                      height="30"
                      rx="15"
                      fill="#F42F39"
                    />
                  </g>
                  <text className="train-t" x="440" y="220">
                    CS-001
                  </text>
                  <path d="M440 232 l-6 0 l6 8 l6 -8 Z" fill="#F42F39" />
                </g>
                <text className="foot p9" x="414" y="345">
                  Each gate approved before the train moves on
                </text>
              </g>
              <g className="scene sc4">
                <line
                  x1="825"
                  y1="380"
                  x2="825"
                  y2="440"
                  stroke="#2f378a"
                  strokeWidth="2"
                  strokeDasharray="3 4"
                />
                <g filter="url(#nxCard)">
                  <rect
                    x="615"
                    y="60"
                    width="420"
                    height="320"
                    rx="16"
                    fill="#FFFFFF"
                    stroke="#E2E8F0"
                  />
                </g>
                <circle cx="649" cy="92" r="13" fill="#2f378a" />
                <text className="p-num" x="649" y="97">
                  4
                </text>
                <text className="p-name" x="671" y="97">
                  Execute
                </text>
                <text className="p-sub" x="639" y="120">
                  DEPLOYS AS EACH GATE APPROVES
                </text>
                <line x1="635" y1="134" x2="1015" y2="134" stroke="#E2E8F0" />
                <g className="e0">
                  <text className="env-t" x="643" y="158">
                    DEV
                  </text>
                  <rect
                    x="745"
                    y="150"
                    width="150"
                    height="8"
                    rx="4"
                    fill="#E2E8F0"
                  />
                  <circle
                    cx="925"
                    cy="154"
                    r="11"
                    fill="#fff"
                    stroke="#CBD5E1"
                    strokeWidth="2.5"
                  />
                  <text className="env-t" x="643" y="196">
                    QA
                  </text>
                  <rect
                    x="745"
                    y="188"
                    width="150"
                    height="8"
                    rx="4"
                    fill="#E2E8F0"
                  />
                  <circle
                    cx="925"
                    cy="192"
                    r="11"
                    fill="#fff"
                    stroke="#CBD5E1"
                    strokeWidth="2.5"
                  />
                  <text className="env-t" x="643" y="234">
                    UAT
                  </text>
                  <rect
                    x="745"
                    y="226"
                    width="150"
                    height="8"
                    rx="4"
                    fill="#E2E8F0"
                  />
                  <circle
                    cx="925"
                    cy="230"
                    r="11"
                    fill="#fff"
                    stroke="#CBD5E1"
                    strokeWidth="2.5"
                  />
                  <text className="env-t" x="643" y="272">
                    Pre-Prod
                  </text>
                  <rect
                    x="745"
                    y="264"
                    width="150"
                    height="8"
                    rx="4"
                    fill="#E2E8F0"
                  />
                  <circle
                    cx="925"
                    cy="268"
                    r="11"
                    fill="#fff"
                    stroke="#CBD5E1"
                    strokeWidth="2.5"
                  />
                  <text className="env-tp" x="643" y="310">
                    Production
                  </text>
                  <rect
                    x="745"
                    y="302"
                    width="150"
                    height="8"
                    rx="4"
                    fill="#E2E8F0"
                  />
                  <circle
                    cx="925"
                    cy="306"
                    r="11"
                    fill="#fff"
                    stroke="#CBD5E1"
                    strokeWidth="2.5"
                  />
                </g>
                <rect
                  className="eb1"
                  x="745"
                  y="150"
                  width="150"
                  height="8"
                  rx="4"
                  fill="#2f378a"
                />
                <g className="et1">
                  <circle cx="925" cy="154" r="11" fill="#2f378a" />
                  <polyline
                    points="920,154 924,158 931,148"
                    fill="none"
                    stroke="#fff"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </g>
                <rect
                  className="eb2"
                  x="745"
                  y="188"
                  width="150"
                  height="8"
                  rx="4"
                  fill="#2f378a"
                />
                <g className="et2">
                  <circle cx="925" cy="192" r="11" fill="#2f378a" />
                  <polyline
                    points="920,192 924,196 931,186"
                    fill="none"
                    stroke="#fff"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </g>
                <rect
                  className="eb3"
                  x="745"
                  y="226"
                  width="150"
                  height="8"
                  rx="4"
                  fill="#2f378a"
                />
                <g className="et3">
                  <circle cx="925" cy="230" r="11" fill="#2f378a" />
                  <polyline
                    points="920,230 924,234 931,224"
                    fill="none"
                    stroke="#fff"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </g>
                <rect
                  className="eb4"
                  x="745"
                  y="264"
                  width="150"
                  height="8"
                  rx="4"
                  fill="#2f378a"
                />
                <g className="et4">
                  <circle cx="925" cy="268" r="11" fill="#2f378a" />
                  <polyline
                    points="920,268 924,272 931,262"
                    fill="none"
                    stroke="#fff"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </g>
                <rect
                  className="eb5"
                  x="745"
                  y="302"
                  width="150"
                  height="8"
                  rx="4"
                  fill="#2f378a"
                />
                <g className="et5">
                  <circle cx="925" cy="306" r="11" fill="#2f378a" />
                  <polyline
                    points="920,306 924,310 931,300"
                    fill="none"
                    stroke="#fff"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </g>
                <text className="foot e9" x="643" y="350" fill="#2f378a">
                  Every record logged with its Oracle response
                </text>
              </g>
              <g className="scene sc5">
                <line
                  x1="1050"
                  y1="380"
                  x2="1050"
                  y2="440"
                  stroke="#2f378a"
                  strokeWidth="2"
                  strokeDasharray="3 4"
                />
                <g filter="url(#nxCard)">
                  <rect
                    x="740"
                    y="60"
                    width="420"
                    height="320"
                    rx="16"
                    fill="#FFFFFF"
                    stroke="#E2E8F0"
                  />
                </g>
                <g className="f1">
                  <circle cx="950" cy="170" r="46" fill="#2f378a" />
                  <polyline
                    points="930,170 944,184 971,153"
                    fill="none"
                    stroke="#fff"
                    strokeWidth="6.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </g>
                <circle
                  className="f1r"
                  cx="950"
                  cy="170"
                  r="46"
                  fill="#2f378a"
                />
                <circle
                  className="sp1"
                  cx="880"
                  cy="136"
                  r="4"
                  fill="#2f378a"
                  opacity=".55"
                />
                <circle
                  className="sp2"
                  cx="1016"
                  cy="130"
                  r="5"
                  fill="#2f378a"
                  opacity=".55"
                />
                <circle
                  className="sp3"
                  cx="892"
                  cy="214"
                  r="4.5"
                  fill="#2f378a"
                  opacity=".55"
                />
                <circle
                  className="sp4"
                  cx="1022"
                  cy="206"
                  r="4"
                  fill="#2f378a"
                  opacity=".55"
                />
                <text className="win-t f2" x="950" y="252">
                  Live in Production
                </text>
                <text className="win-s f3" x="950" y="278">
                  All five environments in sync
                </text>
                <g className="ch1">
                  <rect
                    x="782"
                    y="298"
                    width="118"
                    height="32"
                    rx="16"
                    fill="#EAF0FF"
                  />
                  <text className="chip-t" x="841.0" y="319" fill="#2f378a">
                    5 environments
                  </text>
                </g>
                <g className="ch2">
                  <rect
                    x="910"
                    y="298"
                    width="104"
                    height="32"
                    rx="16"
                    fill="#EAF0FF"
                  />
                  <text className="chip-t" x="962.0" y="319" fill="#2f378a">
                    2 records
                  </text>
                </g>
                <g className="ch3">
                  <rect
                    x="1024"
                    y="298"
                    width="94"
                    height="32"
                    rx="16"
                    fill="#EAF0FF"
                  />
                  <text className="chip-t" x="1071.0" y="319" fill="#2f378a">
                    0 errors
                  </text>
                </g>
                <text className="foot f4" x="950" y="358" textAnchor="middle">
                  &#8634; One-click rollback still available
                </text>
              </g>
            </svg>
          </div>
        </div>
      </section>

      {/* ================= POSITIONING BAND ================= */}
      <div className="band">
        <div className="wrap" data-reveal>
          <span className="marker"></span>
          <p>
            During implementation, Nexus for Oracle Fusion speeds up delivery
            for system integrators. After go-live, it de-risks every change and{" "}
            <b>cuts the cost of keeping client environments in sync.</b>
          </p>
        </div>
      </div>

      {/* ================= PROBLEM ================= */}
      <section id="problem">
        <div className="wrap">
          <div className="sec-head" data-reveal>
            <p className="eyebrow !text-[#2f378a]">
              The hidden tax of post-go-live change
            </p>
            <h2>
              Oracle Fusion gives you no clean way to promote <em>selected</em>{" "}
              changes.
            </h2>
            <p className="lead" style={{ marginTop: "16px" }}>
              Once you're live, every configuration change turns into manual
              detective work. Someone has to find what changed, decide what
              should move, move only that, get it approved with visibility, and
              prove it landed — across five environments, by hand.
            </p>
          </div>
          <div className="grid cols-3">
            <div className="card" data-reveal>
              <div className="ic">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#F42F39"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </div>
              <h3>All-or-nothing export</h3>
              <p>
                Setup &amp; Maintenance moves an entire task or offering. No
                cherry-pick, no delta-only package.
              </p>
            </div>
            <div className="card" data-reveal>
              <div className="ic">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#F42F39"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 7h-9M14 17H5M17 3l3 4-3 4M7 21l-3-4 3-4" />
                </svg>
              </div>
              <h3>Manual assembly</h3>
              <p>
                Oracle's compare can diff an export, but collecting just the
                changes you want to promote is many manual steps — and still
                can't produce a delta-only import.
              </p>
            </div>
            <div className="card" data-reveal>
              <div className="ic">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#F42F39"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
                  <circle cx="12" cy="12" r="3" />
                  <path d="M3 3l18 18" />
                </svg>
              </div>
              <h3>No UAT-vs-Prod visibility</h3>
              <p>
                There's no single view of how your environments actually differ.
              </p>
            </div>
            <div className="card" data-reveal>
              <div className="ic">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#F42F39"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 11l3 3L22 4" />
                  <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                </svg>
              </div>
              <h3>Blind approvals</h3>
              <p>
                Change requests flow through ServiceNow-style tools, but
                approvers never see the actual config being migrated. They sign
                off blind.
              </p>
            </div>
            <div className="card" data-reveal>
              <div className="ic">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#F42F39"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M14 4l-1 2M6 20l1-2M4 10H2M20 14h2M12 2v2" />
                  <path d="M7 14a5 5 0 0 1 10 0" />
                  <path d="M5 20h14" />
                </svg>
              </div>
              <h3>Silent config drift</h3>
              <p>
                Post-go-live changes get applied by hand, with no record of what
                changed.
              </p>
            </div>
            <div className="card" data-reveal>
              <div className="ic">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#F42F39"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3 3v5h5" />
                  <path d="M3.05 13A9 9 0 1 0 6 5.3L3 8" />
                  <path d="M12 7v5l3 2" />
                </svg>
              </div>
              <h3>No audit memory</h3>
              <p>
                Months later, no one can say who changed what in Production, or
                why.
              </p>
            </div>
          </div>
          <div className="note" data-reveal>
            The result: environments silently drift out of sync, releases slip,
            and audits become fire drills.
          </div>
        </div>
      </section>

      {/* ================= SOLUTION ================= */}
      <section
        id="solution"
        style={{
          background: "var(--surface)",
          borderTop: "1px solid var(--border)",
          borderBottom: "1px solid var(--border)",
        }}
      >
        <div className="wrap">
          <div className="sec-head" data-reveal>
            <p className="eyebrow !text-[#2f378a]">The idea</p>
            <h2>Treat Fusion configuration like source code.</h2>
            <p className="lead" style={{ marginTop: "16px" }}>
              Nexus gives configuration the disciplines software delivery
              already takes for granted — version control, diffs, pull requests,
              pipelines, and rollback — purpose-built for Oracle Fusion.
            </p>
          </div>
          <div className="map" data-reveal>
            <div className="mh a">Software delivery has…</div>
            <div className="mh b">Nexus brings it to Fusion config</div>
            <div className="mrow left">Version control</div>
            <div className="mrow right">
              A registry of every migratable object + pinned baselines
            </div>
            <div className="mrow left">“git diff”</div>
            <div className="mrow right">
              Automatic discovery of what changed since the last baseline
            </div>
            <div className="mrow left">Pull requests</div>
            <div className="mrow right">
              Reviewable, taggable changesets with approval gates
            </div>
            <div className="mrow left">CI/CD pipelines</div>
            <div className="mrow right">
              Multi-stage promotion DEV → QA → UAT → Pre-Prod → Prod
            </div>
            <div className="mrow left">Rollback</div>
            <div className="mrow right">
              One-click revert to the exact pre-deployment state
            </div>
          </div>
          <p className="punch" data-reveal>
            You stop babysitting config. You govern it.
          </p>
        </div>
      </section>

      {/* ================= HOW IT WORKS ================= */}
      <section id="how">
        <div className="wrap">
          <div className="sec-head" data-reveal>
            <p className="eyebrow !text-[#2f378a]">End to end, in five steps</p>
            <h2>
              Discover → Review → Promote → Execute → Release &amp; Revert
            </h2>
          </div>
          <div className="steps">
            <div className="step" data-reveal>
              <div className="num">1</div>
              <div>
                <h3>Discover</h3>
                <p>
                  One click scans Oracle Fusion and compares it to your pinned
                  baseline. See exactly what changed: added, changed, removed.
                  First run quietly sets the baseline, so you only ever see{" "}
                  <em>real</em> changes.
                </p>
              </div>
            </div>
            <div className="step" data-reveal>
              <div className="num">2</div>
              <div>
                <h3>Review</h3>
                <p>
                  Each change becomes a changeset you can read, tag, and
                  cherry-pick. Exclude what isn't ready — it automatically
                  resurfaces in the next changeset with fresh values, so nothing
                  slips through.
                </p>
              </div>
            </div>
            <div className="step" data-reveal>
              <div className="num">3</div>
              <div>
                <h3>Promote</h3>
                <p>
                  Submit the changeset to a pipeline. It flows through your
                  environments in order, with the right approval at each gate.
                  Approvers see the actual records, not just a ticket title —
                  and can pre-approve so promotions don't wait in line.
                </p>
              </div>
            </div>
            <div className="step" data-reveal>
              <div className="num">4</div>
              <div>
                <h3>Execute</h3>
                <p>
                  Nexus writes the change into Oracle for you. It resolves
                  parent/dependency records in the right order, skips anything
                  that already matches, and logs every single record's outcome
                  with the exact Oracle response. If a step fails, retry from
                  that step — not the whole run.
                </p>
              </div>
            </div>
            <div className="step last" data-reveal>
              <div className="num">5</div>
              <div>
                <h3>Release &amp; Revert</h3>
                <p>
                  Bundle tested changes into a release for Pre-Prod → Prod.
                  Before executing, Nexus pins the current state as a safety
                  snapshot. If something breaks, revert rolls Production back to
                  that exact prior state.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= COVERAGE & ROADMAP ================= */}
      <section
        id="coverage"
        style={{
          background: "var(--surface)",
          borderTop: "1px solid var(--border)",
          borderBottom: "1px solid var(--border)",
        }}
      >
        <div className="wrap">
          <div className="sec-head" data-reveal>
            <p className="eyebrow !text-[#2f378a]">Coverage &amp; roadmap</p>
            <h2>
              Broad coverage today. Every kind of Fusion change on the
              trajectory.
            </h2>
          </div>
          <div className="board">
            <div className="col" data-reveal>
              <div className="pill now">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#fff"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 6L9 17l-5-5" />
                </svg>{" "}
                Available today
              </div>
              <div className="sub">Full Setup &amp; Maintenance coverage</div>
              <div className="item">
                <div className="t">
                  Every Offering, Functional Area, and Task
                </div>
                <div className="d">
                  The complete functional setup surface Oracle supports —
                  governed and audited end to end.
                </div>
              </div>
              <div className="reinforce">
                The entire functional setup surface, migrated and audited.
              </div>
            </div>
            <div className="col" data-reveal>
              <div className="pill prog">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#fff"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.6 2.6-2.4-2.4z" />
                </svg>{" "}
                Work in progress
              </div>
              <div className="sub">Beyond setup data</div>
              <div className="item">
                <div className="t">BI Publisher (BIP) reports</div>
                <div className="d">
                  Promote report definitions across environments.
                </div>
              </div>
              <div className="item">
                <div className="t">Application Composer changes</div>
                <div className="d">
                  Including flexfields and other extensibility configuration.
                </div>
              </div>
              <div className="item">
                <div className="t">Data Business Objects</div>
                <div className="d">
                  Migrate transactional / master data such as PIM Items and SCM
                  Price Lists, not just setup config.
                </div>
              </div>
            </div>
            <div className="col" data-reveal>
              <div className="pill next">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#fff"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M4.5 16.5L3 21l4.5-1.5M12 15l-3-3M15 12l-3-3" />
                  <path d="M9 12c0-4 2-8 8-9 1 6-3 9-6 10z" />
                </svg>{" "}
                On the roadmap
              </div>
              <div className="sub">New frontiers</div>
              <div className="item">
                <div className="t">AI agentic actions ✦</div>
                <div className="d">
                  An agent that summarizes migrated changes and auto-generates a
                  SOX-compliant audit package — the immutable trail, turned into
                  a sign-off-ready document.
                </div>
              </div>
              <div
                className="reinforce"
                style={{
                  background: "var(--primary-soft)",
                  color: "var(--primary)",
                }}
              >
                Audit, turned into a sign-off-ready compliance document.
              </div>
            </div>
          </div>
          <p className="trajectory" data-reveal>
            <b>The trajectory:</b> broad coverage of setup config today, moving
            toward every kind of Fusion change — extensions, data, and
            AI-assisted compliance.
          </p>
        </div>
      </section>

      {/* ================= MAGNITUDE ================= */}
      <section id="magnitude">
        <div className="wrap">
          <div className="sec-head" data-reveal>
            <p className="eyebrow !text-[#2f378a]">The size of the problem</p>
            <h2>
              Every manual change is a tax. Multiply it by your release cadence.
            </h2>
          </div>
          <div className="stats">
            <div className="stat" data-reveal>
              <div className="big">
                <span className="was">~4–6 hrs</span>20–40 min
              </div>
              <div className="cap">
                End-to-end promotion time per change{" "}
                <em>(illustrative; validate in a pilot)</em>
              </div>
            </div>
            <div className="stat" data-reveal>
              <div className="big">
                <span data-count="5">0</span> environments
              </div>
              <div className="cap">
                DEV → QA → UAT → Pre-Prod → Prod, every change re-done by hand
                at each hop today
              </div>
            </div>
            <div className="stat" data-reveal>
              <div className="big">
                <span data-count="100">0</span>% audit coverage
              </div>
              <div className="cap">
                Every change, approval, and deployment logged immutably
              </div>
            </div>
          </div>
          <div className="tbl-wrap" data-reveal>
            <table>
              <thead>
                <tr>
                  <th>Activity</th>
                  <th>Before (manual)</th>
                  <th>With Nexus</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Discover what changed</td>
                  <td>
                    Hours of CSV juggling — chasing every functional consultant
                    to collect the changes
                  </td>
                  <td>Minutes</td>
                </tr>
                <tr>
                  <td>Approve a promotion</td>
                  <td>
                    Blind sign-off after 1–4 hr round-trips — pages of
                    documentation to read just to understand the change
                  </td>
                  <td>Approvers see the delta; pre-approved</td>
                </tr>
                <tr>
                  <td>Promote one stage</td>
                  <td>
                    4–8 hrs of documentation and applying every change by hand
                  </td>
                  <td>Auto-routed</td>
                </tr>
                <tr>
                  <td>Recover from a failed import</td>
                  <td>Manual errors that cost dearly in Production</td>
                  <td>15–30 min (retry from step)</td>
                </tr>
                <tr>
                  <td>“Who deployed this to Prod?”</td>
                  <td>
                    Screen-by-screen comparison of “last updated by” and date
                    columns
                  </td>
                  <td>One query</td>
                </tr>
                <tr>
                  <td>Roll back a bad release</td>
                  <td>All-hands scramble</td>
                  <td>Governed one-click revert</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="frame-line" data-reveal>
            A team pushing even a handful of changes a month across five
            environments burns dozens of hours on mechanics alone — before
            counting the cost of a single bad release or a failed audit. Nexus
            turns those hours into minutes and that risk into a reversible,
            logged event.
          </p>
        </div>
      </section>

      {/* ================= WHY IT MATTERS ================= */}
      <section
        id="why"
        style={{
          background: "var(--surface)",
          borderTop: "1px solid var(--border)",
          borderBottom: "1px solid var(--border)",
        }}
      >
        <div className="wrap">
          <div className="sec-head" data-reveal>
            <p className="eyebrow !text-[#2f378a]">The outcome</p>
            <h2>Why it matters to the business</h2>
          </div>
          <div className="why">
            <div className="tile" data-reveal>
              <div className="ic">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#2f378a"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z" />
                </svg>
              </div>
              <h3>Faster releases</h3>
              <p>
                Config moves at pipeline speed, with approvers who can actually
                see what's moving.
              </p>
            </div>
            <div className="tile" data-reveal>
              <div className="ic">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#2f378a"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
              </div>
              <h3>Fewer outages</h3>
              <p>
                Drift is caught before it reaches Production; bad releases are
                reversible.
              </p>
            </div>
            <div className="tile" data-reveal>
              <div className="ic">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#2f378a"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 11l3 3L22 4" />
                  <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                </svg>
              </div>
              <h3>Audit-ready, always</h3>
              <p>
                Every change, approval, and deployment logged immutably. No more
                audit fire drills.
              </p>
            </div>
            <div className="tile" data-reveal>
              <div className="ic">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#2f378a"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="9" cy="7" r="4" />
                  <path d="M17 11l2 2 4-4" />
                  <path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" />
                </svg>
              </div>
              <h3>Less key-person risk</h3>
              <p>
                The process lives in the platform, not in one expert's head.
              </p>
            </div>
            <div className="tile" data-reveal>
              <div className="ic">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#2f378a"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <ellipse cx="12" cy="5" rx="9" ry="3" />
                  <path d="M3 5v14c0 1.7 4 3 9 3s9-1.3 9-3V5" />
                  <path d="M3 12c0 1.7 4 3 9 3s9-1.3 9-3" />
                </svg>
              </div>
              <h3>Cloud-ready &amp; DB-agnostic</h3>
              <p>
                Runs as a Docker container on Kubernetes, against any RDBMS —
                PostgreSQL, MySQL, or Oracle ATP. Credentials secured via Vault,
                never stored in the app. Onboarding takes hours, and you deploy
                in your preferred cloud: GCP, Azure, AWS, or OCI.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ================= FINAL CTA ================= */}
      <section id="cta" className="final">
        <div className="wrap" data-reveal>
          <p
            className="eyebrow !text-[#2f378a]"
            style={{ color: "var(--accent-soft)" }}
          >
            One governed pipeline
          </p>
          <h2>
            From a manual, risky chore to a governed, auditable, push-button
            pipeline.
          </h2>
          <p className="lead">
            Nexus for Oracle Fusion saves your team hours on every change and
            gives leadership confidence on every release. Book a 30-minute live
            walkthrough on one of your functional areas — discover, review,
            promote, and revert, end to end.
          </p>
          <a
            className="btn btn-primary"
            href="mailto:info@techprojects.com?subject=Nexus%20for%20Oracle%20Fusion%20walkthrough"
          >
            Book the walkthrough →
          </a>
        </div>
      </section>
    </>
  );
};

export default NexusOracleFusion;
